import api from './api';

// ---- Candidate shapes returned by POST /orders/import ----

export interface ImportItem {
  description: string;
  quantity: number;
  weight: number;
  price: number;
  madeIn: string;
  fullDescription: string;
  madeOf: string;
  endUse: string;
  commodityCode: string;
  countryManufactured: string;
}

export interface ImportParcel {
  quantity: number;
  weight: number;
  length: number;
  width: number;
  height: number;
  physicalWeight: number;
  dimensionalWeight: number;
  items: ImportItem[];
}

export interface ImportCustoms {
  exportReason: string;
  dutyAndTaxes: string;
  insuranceAmount: number;
  vatNumber: string;
  receiptToName: string;
  receiptToAddress: string;
  receiptToEmail: string;
  documentDescription?: string;
}

export interface ImportCandidate {
  rowNumber: number;
  reference: string;
  isDocument: boolean;
  currency: string;
  addresses: { from: Record<string, any>; to: Record<string, any> };
  parcels: ImportParcel[];
  customs: ImportCustoms;
  errors: string[];
  warnings: string[];
}

export interface ImportParseSummary {
  totalRows: number;
  validRows: number;
  rowsWithErrors: number;
  rowsWithWarnings: number;
}

export interface ImportParseResult {
  summary: ImportParseSummary;
  orders: ImportCandidate[];
}

// ---- Batch submit shapes for POST /orders/batch ----

export interface BatchOrderInput {
  save_as_draft: boolean;
  addresses: { from: Record<string, any>; to: Record<string, any> };
  parcels: ImportParcel[];
  customs: ImportCustoms;
  isDocument: boolean;
  currency: string;
  referenceNumber?: string | null;
  selectedRate: Record<string, any> | null;
  price: { shipping: number; insurance: number; tax: number; total: number } | null;
  carrier: string;
  service: string;
  deliveryEstimate?: string | null;
}

export interface BatchSubmitPayload {
  paymentMethod: 'wallet' | 'card';
  paymentMethodId?: string | null;
  default_shipping_id: string;
  default_shipping_name: string;
  orders: BatchOrderInput[];
}

export interface BatchSubmitResult {
  drafts: any[];
  submitted: any[];
  failures: { index: number; type: string; error: string }[];
  payment: { method: string; currency: string; total: number; transactionId: string | null; orderCount: number } | null;
}

export interface ExportRateRow {
  rowNumber: number;
  carrier?: string;
  service?: string;
  price?: number | null;
  currency?: string;
  transit?: string;
  mode?: string;
  status?: string;
  notes?: string;
}

const orderImportService = {
  /**
   * Download the user's uploaded sheet with the review-step rate/selection columns appended.
   */
  exportWithRates: async (file: File, rows: ExportRateRow[]): Promise<void> => {
    const form = new FormData();
    form.append('file', file);
    form.append('rows', JSON.stringify(rows));
    const response = await api.post('/orders/import/export', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.download = `${file.name.replace(/\.[^.]+$/, '')}-rates.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  /**
   * Download the import template (.xlsx) and trigger a browser save.
   */
  downloadTemplate: async (): Promise<void> => {
    const response = await api.get('/orders/import/template', { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'order-import-template.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  /**
   * Upload a spreadsheet and get back parsed + validated candidate orders.
   * `maxRows` (optional) caps the number of data rows the server will accept.
   */
  parseImport: async (file: File, options?: { maxRows?: number }): Promise<ImportParseResult> => {
    const form = new FormData();
    form.append('file', file);
    if (options?.maxRows != null) form.append('max_rows', String(options.maxRows));
    const response = await api.post('/orders/import', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data;
  },

  /**
   * Create a batch of orders (drafts and/or submitted) with one shared payment.
   */
  submitBatch: async (payload: BatchSubmitPayload): Promise<BatchSubmitResult> => {
    const response = await api.post('/orders/batch', payload);
    return response.data.data;
  },
};

export default orderImportService;
